/**
 * Bausteine der Oberfläche.
 *
 * Bewusst wenige und einfache: Was hier fehlt, wird an der Fundstelle gebaut.
 * Eine Bibliothek mit dreißig Varianten, von denen fünf benutzt werden, ist
 * kein Designsystem, sondern Ballast.
 */

import { AnimatePresence, motion } from 'framer-motion'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { pressMorph, springSnappy, springSoft } from '../lib/motion'
import './ui.css'

// ------------------------------------------------------------ Schaltfläche --

type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'danger'

export function Button({
  children,
  variant = 'filled',
  block = false,
  small = false,
  busy = false,
  type = 'button',
  className = '',
  disabled,
  ...rest
}: {
  children: ReactNode
  variant?: ButtonVariant
  block?: boolean
  small?: boolean
  busy?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const classes = [
    'btn',
    `btn--${variant}`,
    block ? 'btn--block' : '',
    small ? 'btn--small' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <motion.button
      type={type}
      className={classes}
      disabled={disabled || busy}
      // Shape-Morphing: Die Pille wird beim Drücken zum Squircle.
      whileTap={pressMorph().whileTap}
      transition={springSnappy()}
      {...(rest as React.ComponentProps<typeof motion.button>)}
    >
      {busy && <span className="spinner" aria-hidden="true" />}
      {children}
    </motion.button>
  )
}

// ----------------------------------------------------------------- Eingabe --

export function TextField({
  label,
  hint,
  error,
  ...rest
}: {
  label: string
  hint?: string
  error?: string
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  return (
    <div className={`field ${error ? 'field--invalid' : ''}`}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field__control"
        aria-invalid={error ? true : undefined}
        // Der Fehler wird an das Feld gebunden, nicht bloß daneben rot
        // gefärbt — sonst erfährt ein Screenreader nie, was nicht stimmt.
        aria-describedby={[error ? errorId : null, hint ? hintId : null]
          .filter(Boolean)
          .join(' ')
          .trim() || undefined}
        {...rest}
      />
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
      {hint && !error && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  )
}

export function SelectField({
  label,
  hint,
  children,
  ...rest
}: {
  label: string
  hint?: string
  children: ReactNode
} & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId()
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <select id={id} className="field__control" {...rest}>
        {children}
      </select>
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  )
}

// ------------------------------------------------------------------ Karte ---

export function Card({
  children,
  title,
  action,
  outlined = false,
}: {
  children: ReactNode
  title?: ReactNode
  action?: ReactNode
  outlined?: boolean
}) {
  return (
    <section className={`card ${outlined ? 'card--outlined' : ''}`}>
      {(title || action) && (
        <header className="card__header">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {action}
        </header>
      )}
      {children}
    </section>
  )
}

export function Chip({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger'
}) {
  return <span className={`chip ${tone === 'neutral' ? '' : `chip--${tone}`}`}>{children}</span>
}

export function Banner({
  children,
  tone = 'info',
}: {
  children: ReactNode
  tone?: 'info' | 'error' | 'success' | 'warning'
}) {
  return (
    <div className={`banner banner--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  )
}

export function Spinner({ label = 'Wird geladen …' }: { label?: string }) {
  return (
    <div className="center-page">
      <div className="row">
        <span className="spinner" aria-hidden="true" />
        <span>{label}</span>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------- Dialog ---

export function Dialog({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return

    const previous = document.activeElement as HTMLElement | null
    // Scrollen sperren, solange der Dialog offen ist — sonst rutscht die
    // Seite darunter weg, wenn der Dialog selbst scrollt.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !panel.current) return

      // Fokusfalle: Ohne sie tabbt man aus dem Dialog heraus auf die Seite
      // dahinter, die man gerade nicht bedienen kann.
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    // Erstes Element fokussieren, damit die Tastatur sofort im Dialog ist.
    requestAnimationFrame(() => {
      panel.current?.querySelector<HTMLElement>('input, button, select')?.focus()
    })

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      previous?.focus()
    }
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="scrim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose()
          }}
        >
          <motion.div
            ref={panel}
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={springSoft()}
          >
            <h2 id={titleId} style={{ marginBottom: 'var(--sp-4)' }}>
              {title}
            </h2>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// ---------------------------------------------------------------- Meldungen -

type Toast = { id: number; message: string; tone: 'neutral' | 'success' | 'error' }

const ToastContext = createContext<(message: string, tone?: Toast['tone']) => void>(() => {})

export function useToast() {
  return useContext(ToastContext)
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const counter = useRef(0)

  const show = useCallback((message: string, tone: Toast['tone'] = 'neutral') => {
    const id = ++counter.current
    setToasts((current) => [...current, { id, message, tone }])
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, 5000)
  }, [])

  const value = useMemo(() => show, [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* `aria-live=polite`: Die Meldung wird vorgelesen, unterbricht aber
          nicht, was gerade gesprochen wird. */}
      <div className="toast-area" role="status" aria-live="polite">
        <AnimatePresence initial={false}>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              className={`toast ${toast.tone === 'neutral' ? '' : `toast--${toast.tone}`}`}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={springSoft()}
              layout
            >
              {toast.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}
